<template>
  <application-layout>
    <v-main>
      <v-container fluid>
        <h1>Manage Reviews</h1>
        <v-data-table
          class="mt-2"
          :headers="headers"
          :items="reviews"
          :loading="loading"
        >
          <template v-slot:body="{ items }">
            <transition-group
              name="scroll-y-transition"
              tag="tbody"
              v-if="items.length"
            >
              <tr v-for="item in items" :key="item.created_at">
                <td>{{ item.facility.location_code }}</td>
                <td>{{ item.first_name }} {{ item.last_name }}</td>
                <td>{{ item.email }}</td>
                <td>{{ item.rating }}</td>
                <td>{{ formatDate(item.created_at) }}</td>
                <td>{{ eval(item.judged) }}</td>
                <td>
                  <v-tooltip
                    bottom
                    :disabled="item.content ? item.content.length <= 75 : ''"
                  >
                    <template v-slot:activator="{on}">
                      <p class="content-column mt-4" v-on="on">
                        {{ truncateContent(item.content, 75) }}
                      </p>
                    </template>
                    <span>{{ item.content }}</span>
                  </v-tooltip>
                </td>
                <td>
                  <v-btn
                    data-testid="reviews-approval-approve-btn"
                    class="reviews-approval-approve-btn mr-3"
                    color="success"
                    small
                    @click="handleApprove(item)"
                  >
                    Approve
                  </v-btn>
                  <v-btn
                    data-testid="reviews-approval-deny-btn"
                    class="reviews-approval-deny-btn mr-3"
                    color="error"
                    v-show="item.judged === 0"
                    small
                    @click="handleDeny(item)"
                  >
                    Deny
                  </v-btn>
                  <v-btn
                    data-testid="reviews-approval-edit-btn"
                    class="reviews-approval-edit-btn"
                    color="primary"
                    small
                    @click="openEditModal(item)"
                  >
                    Edit
                  </v-btn>
                </td>
              </tr>
            </transition-group>
            <tr v-else>
              <td colspan="12" style="text-align: center">
                No currently-eligible reviews found.
              </td>
            </tr>
          </template>
        </v-data-table>
        <div class="subheading my-3 mx-1">
          Reviews marked as "Denied" will remain in the approval queue until the end of the day.
        </div>
      </v-container>
    </v-main>

    <delete-modal
      v-model="modal"
      :noButton="true"
      @delete="actionType === 'approve' ? enableReview() : disableReview()"
      @collapse="modal=false"
      :itemType="'review'"
      :actionType="actionType"
      :advisory="advisory"
    />

    <v-dialog
      v-model="editReview"
      persistent
      max-width="600"
    >
      <v-card>
        <v-card-title class="headline">Edit Review</v-card-title>
        <v-divider />
        <v-form ref="form">
          <v-card-text>
            <v-col>
              <v-row>
                <v-col cols="6" class="ml-1">
                  <app-input
                    data-testid="reviews-approval-first-name-input"
                    class="reviews-approval-first-name-input"
                    :type="'text'"
                    label="First Name"
                    v-model="selectedReview.first_name"
                  />
                </v-col>
                <v-col cols="6" class="ml-1">
                  <app-input
                    data-testid="reviews-approval-last-name-input"
                    class="reviews-approval-last-name-input"
                    :type="'text'"
                    label="Last Name"
                    v-model="selectedReview.last_name"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="6" class="ml-1">
                  <app-input
                    data-testid="reviews-approval-email-input"
                    class="reviews-approval-email-input"
                    :type="'email'"
                    label="Email"
                    v-model="selectedReview.email"
                    :rules="{ email: true }"
                  />
                </v-col>
                <v-col cols="6" class="mr-1">
                    <span class="ml-2 caption grey--text text--lighten-2">Rating</span>
                    <v-rating
                      data-testid="reviews-approval-rating"
                      class="reviews-approval-rating"
                      v-model="selectedReview.rating"
                      background-color="primary"
                      color="primary"
                      hover
                      medium
                      readonly
                    />
                </v-col>
              </v-row>
              <v-row class="mb-4 ml-1 justify-middle" >
                <v-radio-group
                  label="Featured on Homepage:"
                  v-model="selectedReview.featured_on_homepage"
                  hide-details
                  row
                >
                  <v-radio
                    data-testid="reviews-approval-featured-radio"
                    class="reviews-approval-featured-radio ml-2"
                    label="Featured"
                    :value=1
                  />
                  <v-radio
                    data-testid="reviews-approval-not-featured-radio"
                    class="reviews-approval-not-featured-radio"
                    label="Not Featured"
                    :value=0
                  />
                </v-radio-group>
              </v-row>
              <v-textarea
                data-testid="reviews-approval-content-textarea"
                class="reviews-approval-content-textarea"
                label="Content"
                filled
                v-model="selectedReview.content"
                clearable
              />
            </v-col>
          </v-card-text>
          <v-divider />
          <v-card-actions>
            <v-btn
              data-testid="reviews-approval-cancel-btn"
              class="reviews-approval-cancel-btn"
              color="error"
              @click="closeEditModal"
            >
              Cancel
            </v-btn>
            <v-spacer />
            <v-btn
                data-testid="reviews-approval-update-btn"
                class="reviews-approval-update-btn"
                color="primary"
                @click="updateReviewInQueue(selectedReview)"
                :loading="updateLoading"
            >
              Update
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </application-layout>
</template>

<script>
import { approveAdvisoryText, denyAdvisoryText } from "@/helpers/reviews-approval/approve-deny-advisory-text";
import AppInput from "@/components/global/AppInput";
import ApplicationLayout from "@/layouts/ApplicationLayout";
import DeleteModal from "@/components/DeleteModal";
import { formatDate } from "@/helpers/functions/format-date";
import { reviewsApprovalHeaders } from "@/helpers/reviews-approval/reviews-approval-data-table-headers";
import { truncateContent } from "@/helpers/functions/truncate-content";

export default {
  data() {
    return {
      loading: false,
      updateLoading: false,
      editReview: false,
      modal: false,

      selectedReview: "",
      actionType: "",
      advisory: "",

      headers: reviewsApprovalHeaders
    };
  },

  methods: {
    formatDate: formatDate,
    truncateContent: truncateContent,
    handleApprove(review) {
      this.modal = true;
      this.selectedReview = review;
      this.actionType = "approve";
      this.advisory = approveAdvisoryText;
    },

    handleDeny(review) {
      this.modal = true;
      this.selectedReview = review;
      this.actionType = "deny";
      this.advisory = denyAdvisoryText;
    },

    openEditModal(review) {
      this.editReview = true;
      this.selectedReview = review;
    },

    closeEditModal() {
      this.editReview = false;
      this.selectedReview = "";
    },

    getReviews() {
      this.$store.dispatch("getReviews");
    },

    updateReviewInQueue(payload) {
      this.updateLoading = true;
      this.$store.dispatch("updateReviewInQueue", payload)
        .then(resp => {
          this.updateLoading = false;
          this.closeEditModal();
        })
        .catch(() => {
          this.updateLoading = false;
        });
    },

    enableReview() {
      this.loading = true;
      this.$store.dispatch("enableReview", this.selectedReview.id)
        .then(resp => {
          this.loading = false;
          this.modal = false;
          this.selectedReview = "";
        })
        .catch(() => {
          this.loading = false;
        });
    },

    disableReview() {
      this.loading = true;
      this.$store.dispatch("disableReview", this.selectedReview.id)
        .then(resp => {
          this.loading = false;
          this.modal = false;
          this.selectedReview = "";
        })
        .catch(() => {
          this.loading = false;
        });
    },

    eval(review) {
      return review === 1 ? "Yes" : "No";
    }
  },

  computed: {
    reviews() {
      return this.$store.state.facilityReviews.reviews;
    }
  },

  components: {
    ApplicationLayout,
    AppInput,
    DeleteModal
  },

  created() {
    this.getReviews();
  }
};
</script>

<style>
  .v-tooltip__content {
    background-color: #D7BDE2;
    color: #000;
  }

  .content-column {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    max-width: 25rem;
  }
</style>
